import {
  MutationFunctionOptions,
  OperationVariables,
  QueryResult,
} from "@apollo/react-common";
import {
  LazyQueryHookOptions,
  MutationHookOptions,
  QueryHookOptions,
  QueryLazyOptions,
  useLazyQuery,
  useMutation,
  useQuery,
} from "@apollo/react-hooks";
import { ApolloError } from "apollo-boost";
import { DocumentNode } from "graphql";
import { useCallback, useEffect, useState } from "react";
import {
  isRequestError,
  isRequestLoaded,
  isRequestLoading,
  RequestState,
  RequestStateError,
  RequestStateInitial,
  RequestStateLoaded,
  RequestStateLoading,
} from "../models/RequestState";
import { isNonNullable } from "../utils/null";

export function useGraphqlQuery<TData = never, TVariables = OperationVariables>(
  query: DocumentNode,
  options?: QueryHookOptions<TData, TVariables>
): {
  requestState: RequestState<TData, ApolloError>;
  fetchMore: QueryResult<TData, TVariables>["fetchMore"];
  refetch: QueryResult<TData, TVariables>["refetch"];
  updateQuery: QueryResult<TData, TVariables>["updateQuery"];
} {
  const { loading, error, data, fetchMore, refetch, updateQuery } = useQuery<
    TData,
    TVariables
  >(query, options);
  const [requestState, setRequestState] = useState<
    RequestState<TData, ApolloError>
  >(RequestStateInitial());
  useEffect(() => {
    if (loading && !isRequestLoading(requestState)) {
      setRequestState(RequestStateLoading());
    } else if (isNonNullable(error) && !isRequestError(requestState)) {
      setRequestState(RequestStateError(error));
    }
  }, [loading, error, requestState]);

  useEffect(() => {
    if (isNonNullable(data)) {
      setRequestState(RequestStateLoaded(data));
    }
  }, [data]);

  return {
    requestState,
    fetchMore,
    refetch,
    updateQuery,
  };
}

export function useLazyGraphqlQuery<
  TData = never,
  TVariables = OperationVariables
>(
  query: DocumentNode,
  options?: LazyQueryHookOptions<TData, TVariables>
): {
  requestState: RequestState<TData, ApolloError>;
  fetch: (options?: QueryLazyOptions<TVariables> | undefined) => void;
  fetchMore: QueryResult<TData, TVariables>["fetchMore"];
  refetch: QueryResult<TData, TVariables>["refetch"];
  updateQuery: QueryResult<TData, TVariables>["updateQuery"];
} {
  const [
    fetch,
    { loading, fetchMore, refetch, error, data, updateQuery },
  ] = useLazyQuery<TData, TVariables>(query, options);
  const [requestState, setRequestState] = useState<
    RequestState<TData, ApolloError>
  >(RequestStateInitial());
  useEffect(() => {
    if (!loading && (isNonNullable(data) || isRequestLoaded(requestState))) {
      return;
    }
    if (loading && !isRequestLoading(requestState) && !isNonNullable(data)) {
      setRequestState(RequestStateLoading());
    } else if (isNonNullable(error) && !isRequestError(requestState)) {
      setRequestState(RequestStateError(error));
    }
  }, [loading, error, requestState, data]);

  useEffect(() => {
    if (isNonNullable(data)) {
      setRequestState(RequestStateLoaded(data));
    }
  }, [data]);

  return { requestState, fetch, fetchMore, refetch, updateQuery };
}

export function useGraphqlMutation<
  TData = never,
  TVariables = OperationVariables
>(
  mutation: DocumentNode,
  options?: MutationHookOptions<TData, TVariables>
): (
  options?: MutationFunctionOptions<TData, TVariables>
) => Promise<TData | undefined> {
  const [doMutate] = useMutation<TData, TVariables>(mutation, options);
  return useCallback(
    async (options?: MutationFunctionOptions<TData, TVariables>) => {
      const result = await doMutate(options);
      return result.data;
    },
    [doMutate]
  );
}

export function getExceptionApolloErrorCodes(err: Error): string[] | null {
  if (!(err instanceof ApolloError)) {
    return null;
  }
  const codes: string[] = [];
  for (const gqlErr of err.graphQLErrors) {
    const code = gqlErr.extensions?.code;
    if (typeof code === "string") {
      codes.push(code);
    }
  }
  return codes;
}

// Get first apollo error code if found
export function getExceptionApolloErrorCode(err: Error): string | null {
  const codes = getExceptionApolloErrorCodes(err);
  if (codes == null || codes.length === 0) {
    return null;
  }
  return codes[0];
}
